import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ContentType } from '../types/revision';
import Loading from '../component/Loading/Loading';
import ProtectedRoute from '../ProtectedRoute';
import type { RouteHandle } from './routeTypes';
import { paths } from './paths';
import { TASKBOARD_ENABLED } from '../constants/variables';
import RouteErrorBoundary from '../component/RouteErrorBoundary/RouteErrorBoundary';

// Lazy load components for better performance
const Dashboard = lazy(() => import('../pages/Dashboard/Dashboard'));
const Scheduled = lazy(() => import('../pages/Scheduled/Scheduled'));
const Taskboard = lazy(() => import('../pages/Taskboard/Taskboard'));
const TaskboardArchive = lazy(
  () => import('../pages/Taskboard/TaskboardArchive')
);
const BoardTemplatePicker = lazy(
  () => import('../pages/Taskboard/BoardTemplatePicker')
);
const MyTeam = lazy(() => import('../pages/MyTeam/Myteam'));
const Onboard = lazy(() => import('../pages/Onboard/Onboard'));
const CreateOrganization = lazy(
  () => import('../pages/Organizations/CreateOrganization')
);
const InviteOrgMembers = lazy(
  () => import('../pages/Organizations/InviteOrgMembers')
);
const SelectPlan = lazy(() => import('../pages/SelectPlan/SelectPlan'));
const Updates = lazy(() => import('../pages/Updates/Updates'));
const Login = lazy(() => import('../pages/Login/Login'));
const Register = lazy(() => import('../pages/Register/Register'));
const AccountSetup = lazy(() => import('../pages/AccountSetup/AccountSetup'));
const AcceptOrgInvitation = lazy(
  () => import('../pages/Organizations/AcceptOrgInvitation')
);
const Revisions = lazy(() => import('../pages/Revisions/Revisions'));
const RevisionFolder = lazy(
  () => import('../pages/FolderRevision/FolderRevision')
);
const DraftDetail = lazy(() => import('../pages/DraftDetail/DraftDetail'));
const ProjectDetail = lazy(
  () => import('../pages/ProjectDetail/ProjectDetail')
);
const PublicDraftView = lazy(
  () => import('../pages/PublicDraftView/PublicDraftView')
);
const ResetPassword = lazy(
  () => import('../pages/ResetPassword/ResetPassword')
);
const FlowbiteKitchenSink = lazy(
  () => import('../pages/KitchenSink/FlowbiteKitchenSink')
);
const NotFound = lazy(() => import('../pages/NotFound/NotFound'));
const DashBoardLayout = lazy(() => import('../layouts/DashBoardLayout'));
const RestoreRevision = lazy(
  () => import('../pages/Revisions/RestoreRevision')
);
const SettingsLayout = lazy(() => import('../pages/Settings/SettingsLayout'));
const SettingsWorkspace = lazy(
  () => import('../pages/Settings/SettingsWorkspace')
);
const SettingsUsage = lazy(() => import('../pages/Settings/SettingsUsage'));
const SettingsMembers = lazy(() => import('../pages/Settings/SettingsMembers'));
const SettingsBilling = lazy(() => import('../pages/Settings/SettingsBilling'));
const SettingsChannels = lazy(
  () => import('../pages/Settings/SettingsChannels')
);
const SettingsProfile = lazy(() => import('../pages/Settings/SettingsProfile'));
const SettingsSecurity = lazy(
  () => import('../pages/Settings/SettingsSecurity')
);
const SettingsChannelDetail = lazy(
  () => import('../pages/Settings/SettingsChannelDetail')
);
const SettingsRoles = lazy(() => import('../pages/Settings/SettingsRoles'));
const Checkout = lazy(() => import('../pages/Checkout/Checkout'));
const CheckoutSuccess = lazy(() => import('../pages/Checkout/CheckoutSuccess'));
const NotificationsPage = lazy(
  () => import('../pages/Notifications/NotificationsPage')
);
const HelpPage = lazy(() => import('../pages/Help/HelpPage'));
const FuturePage = lazy(() => import('../pages/Future/FuturePage'));
const SearchPage = lazy(() => import('../pages/Search/SearchPage'));

// Wrapper for lazy components with Suspense
const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<Loading />}>{children}</Suspense>
);

// Revision components with content type
const RevisionsVideos = () => <Revisions contentType={ContentType.VIDEOS} />;
const RevisionsShorts = () => <Revisions contentType={ContentType.SHORTS} />;
const RevisionsAudio = () => <Revisions contentType={ContentType.AUDIO} />;
const RevisionsThumbnails = () => (
  <Revisions contentType={ContentType.IMAGES} />
);
const RevisionsDocuments = () => (
  <Revisions contentType={ContentType.DOCUMENTS} />
);

export const router = createBrowserRouter([
  // Public routes
  {
    path: paths.root,
    element: (
      <SuspenseWrapper>
        <Login />
      </SuspenseWrapper>
    ),
  },
  {
    path: paths.login,
    element: (
      <SuspenseWrapper>
        <Login />
      </SuspenseWrapper>
    ),
  },
  {
    path: paths.register,
    element: (
      <SuspenseWrapper>
        <Register />
      </SuspenseWrapper>
    ),
  },
  {
    path: paths.signup,
    element: (
      <SuspenseWrapper>
        <Register />
      </SuspenseWrapper>
    ),
  },
  {
    path: paths.resetPassword,
    element: (
      <SuspenseWrapper>
        <ResetPassword />
      </SuspenseWrapper>
    ),
  },
  {
    path: paths.kitchenSink,
    element: (
      <SuspenseWrapper>
        <FlowbiteKitchenSink />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/account-setup/:token',
    element: (
      <SuspenseWrapper>
        <AccountSetup />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/organizations/:orgId/accept-invitation',
    element: (
      <SuspenseWrapper>
        <AcceptOrgInvitation />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/share/d/:shareToken',
    element: (
      <SuspenseWrapper>
        <PublicDraftView />
      </SuspenseWrapper>
    ),
  },

  // Protected routes without dashboard layout
  {
    path: paths.createOrganization,
    element: (
      <SuspenseWrapper>
        <ProtectedRoute
          isOrganizationCheckRequired={false}
          isSubscriptionCheckRequired={false}
        >
          <CreateOrganization />
        </ProtectedRoute>
      </SuspenseWrapper>
    ),
  },
  {
    path: paths.inviteOrgMembers,
    element: (
      <SuspenseWrapper>
        <ProtectedRoute isSubscriptionCheckRequired={false}>
          <InviteOrgMembers />
        </ProtectedRoute>
      </SuspenseWrapper>
    ),
  },
  {
    path: paths.selectPlan,
    element: (
      <SuspenseWrapper>
        <ProtectedRoute isSubscriptionCheckRequired={false}>
          <SelectPlan />
        </ProtectedRoute>
      </SuspenseWrapper>
    ),
  },
  {
    path: paths.onboarding,
    element: (
      <SuspenseWrapper>
        <ProtectedRoute isSubscriptionCheckRequired={false}>
          <Onboard />
        </ProtectedRoute>
      </SuspenseWrapper>
    ),
  },
  // Protected routes with dashboard layout - /tasks.
  // Env-gated: with the taskboard off the whole subtree is replaced by a splat
  // route that bounces deep links home, so none of the pages stay reachable.
  TASKBOARD_ENABLED
    ? {
        path: paths.tasks.root,
        element: (
          <SuspenseWrapper>
            <ProtectedRoute>
              <DashBoardLayout />
            </ProtectedRoute>
          </SuspenseWrapper>
        ),
        handle: { requiresChannel: true } satisfies RouteHandle,
        errorElement: <RouteErrorBoundary />,
        children: [
          // Bare /tasks resolves to the channel's first board (Taskboard redirects).
          { index: true, element: <Taskboard /> },
          // Must precede :boardSlug so it isn't swallowed as a slug.
          { path: 'new', element: <BoardTemplatePicker /> },
          { path: ':boardSlug', element: <Taskboard /> },
          { path: ':boardSlug/archive', element: <TaskboardArchive /> },
        ],
      }
    : {
        // Splat so /tasks and every child path redirect, not just the root.
        path: `${paths.tasks.root}/*`,
        element: <Navigate to={paths.home} replace />,
      },
  {
    path: paths.myteam,
    element: (
      <SuspenseWrapper>
        <ProtectedRoute>
          <MyTeam />
        </ProtectedRoute>
      </SuspenseWrapper>
    ),
    handle: { requiresChannel: true } satisfies RouteHandle,
  },
  {
    path: paths.updates,
    element: (
      <SuspenseWrapper>
        <ProtectedRoute>
          <Updates />
        </ProtectedRoute>
      </SuspenseWrapper>
    ),
  },

  {
    path: '/future',
    element: (
      <SuspenseWrapper>
        <ProtectedRoute>
          <DashBoardLayout />
        </ProtectedRoute>
      </SuspenseWrapper>
    ),
    handle: { requiresChannel: true } satisfies RouteHandle,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <FuturePage />,
      },
    ],
  },

  // Protected routes with dashboard layout - /home
  {
    path: paths.home,
    element: (
      <SuspenseWrapper>
        <ProtectedRoute>
          <DashBoardLayout />
        </ProtectedRoute>
      </SuspenseWrapper>
    ),
    handle: { requiresChannel: true } satisfies RouteHandle,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <Dashboard />,
      },
      {
        path: ':token',
        element: <Dashboard />,
      },
    ],
  },

  // Protected routes with dashboard layout - /search
  // Hides the top header so the page renders its own search bar (matches the
  // design's full-screen search experience).
  {
    path: paths.search,
    element: (
      <SuspenseWrapper>
        <ProtectedRoute>
          <DashBoardLayout />
        </ProtectedRoute>
      </SuspenseWrapper>
    ),
    handle: {
      requiresChannel: true,
      layoutOptions: { hideHeader: true },
    } satisfies RouteHandle,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <SearchPage />,
      },
    ],
  },

  // Protected routes with dashboard layout - /scheduled
  {
    path: paths.scheduled,
    element: (
      <SuspenseWrapper>
        <ProtectedRoute>
          <DashBoardLayout />
        </ProtectedRoute>
      </SuspenseWrapper>
    ),
    handle: { requiresChannel: true } satisfies RouteHandle,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <Scheduled />,
      },
    ],
  },

  // Protected routes with dashboard layout - /projects
  {
    path: paths.projects.root,
    element: (
      <SuspenseWrapper>
        <ProtectedRoute>
          <DashBoardLayout />
        </ProtectedRoute>
      </SuspenseWrapper>
    ),
    handle: { requiresChannel: true } satisfies RouteHandle,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <Revisions />,
      },
      {
        path: 'videos',
        element: <RevisionsVideos />,
      },
      {
        path: 'shorts',
        element: <RevisionsShorts />,
      },
      {
        path: 'audio',
        element: <RevisionsAudio />,
      },
      {
        path: 'thumbnails',
        element: <RevisionsThumbnails />,
      },
      {
        path: 'documents',
        element: <RevisionsDocuments />,
      },
      {
        path: 'restore',
        element: <RestoreRevision />,
      },
      {
        path: 'videos/folder/:folderId',
        element: <RevisionFolder />,
      },
      {
        path: 'shorts/folder/:folderId',
        element: <RevisionFolder />,
      },
      {
        path: 'audio/folder/:folderId',
        element: <RevisionFolder />,
      },
      {
        path: 'thumbnails/folder/:folderId',
        element: <RevisionFolder />,
      },
      {
        path: 'documents/folder/:folderId',
        element: <RevisionFolder />,
      },
      {
        path: ':revisionId',
        element: <ProjectDetail />,
      },
      {
        path: ':revisionId/draft/:draftId',
        element: <DraftDetail />,
        handle: {
          layoutOptions: {
            hideHeader: true,
            defaultCompactSidebar: true,
          },
        } satisfies RouteHandle,
      },
    ],
  },

  // Protected routes with dashboard layout - /dashboard
  // (currently only hosts /dashboard/updates which is a static info page —
  // not channel-gated; do not flag with requiresChannel.)
  {
    path: paths.dashboard.root,
    element: (
      <SuspenseWrapper>
        <ProtectedRoute>
          <DashBoardLayout />
        </ProtectedRoute>
      </SuspenseWrapper>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: 'updates',
        element: <Updates />,
      },
    ],
  },

  // Protected routes with dashboard layout - /help
  {
    path: paths.help,
    element: (
      <SuspenseWrapper>
        <ProtectedRoute isSubscriptionCheckRequired={false}>
          <DashBoardLayout />
        </ProtectedRoute>
      </SuspenseWrapper>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <HelpPage />,
      },
    ],
  },

  // Protected routes with dashboard layout - /notifications
  {
    path: paths.notifications,
    element: (
      <SuspenseWrapper>
        <ProtectedRoute isSubscriptionCheckRequired={false}>
          <DashBoardLayout />
        </ProtectedRoute>
      </SuspenseWrapper>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <NotificationsPage />,
      },
    ],
  },

  // Protected routes with dashboard layout - /settings
  {
    path: paths.settings.root,
    element: (
      <SuspenseWrapper>
        <ProtectedRoute isSubscriptionCheckRequired={false}>
          <DashBoardLayout />
        </ProtectedRoute>
      </SuspenseWrapper>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <SettingsLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="workspace" replace />,
          },
          { path: 'workspace', element: <SettingsWorkspace /> },
          { path: 'usage', element: <SettingsUsage /> },
          { path: 'members', element: <SettingsMembers /> },
          { path: 'billing', element: <SettingsBilling /> },
          { path: 'channels', element: <SettingsChannels /> },
          {
            path: 'channels/:channelId',
            element: <Navigate to="details" replace />,
          },
          {
            path: 'channels/:channelId/details',
            element: <SettingsChannelDetail />,
          },
          {
            path: 'channels/:channelId/members',
            element: <SettingsChannelDetail />,
          },
          { path: 'profile', element: <SettingsProfile /> },
          { path: 'security', element: <SettingsSecurity /> },
          { path: 'roles', element: <SettingsRoles /> },
        ],
      },
    ],
  },

  // Checkout routes (protected, no dashboard layout)
  {
    path: '/checkout/success',
    element: (
      <SuspenseWrapper>
        <ProtectedRoute isSubscriptionCheckRequired={false}>
          <CheckoutSuccess />
        </ProtectedRoute>
      </SuspenseWrapper>
    ),
  },
  {
    path: '/checkout/:planId',
    element: (
      <SuspenseWrapper>
        <Checkout />
      </SuspenseWrapper>
    ),
  },

  // 404 Not Found
  {
    path: '*',
    element: (
      <SuspenseWrapper>
        <NotFound />
      </SuspenseWrapper>
    ),
  },
]);
